import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import { IDENTITY_QUERY_KEY } from '@/entities/session';
import { getOne, getPage, put, remove } from '@/shared/api/http';
import type { Page } from '@/shared/api/http';
import {
  isPermissionCatalogue,
  isRoleList,
  isTenantUser,
} from '@/shared/api/generated/tenancy';
import type {
  PermissionCatalogue,
  RoleList,
  TenantUser,
} from '@/shared/api/generated/tenancy';

/* The tenant's people, its roles, and the permission catalogue behind them.
 *
 * All three routes live under `/v1/auth` rather than an `/v1/admin` prefix: `core.tenancy` owns
 * identity, the edge already has a `RoutePolicy` for that prefix, and a second prefix would split
 * one module's surface across two.
 *
 * **Roles are replaced as a whole set, never granted and revoked in a pair.** `PUT .../roles` takes
 * the full list and the server computes the difference — so "remove admin, add reader" is one
 * request rather than two with an observable window in which the user holds neither or both.
 */

export const USERS_URL = '/v1/auth/users';
export const ROLES_URL = '/v1/auth/roles';
export const PERMISSIONS_URL = '/v1/auth/permissions';

export const USERS_QUERY_KEY = ['admin', 'users'] as const;
export const ROLES_QUERY_KEY = ['admin', 'roles'] as const;
export const PERMISSIONS_QUERY_KEY = ['admin', 'permissions'] as const;

/** One page of users. Each row carries its roles, so a forty-user table is one request. */
export function useTenantUsers(): UseQueryResult<Page<TenantUser>> {
  return useQuery({
    queryKey: USERS_QUERY_KEY,
    queryFn: () => getPage(USERS_URL, isTenantUser, 'tenant user'),
    retry: false,
  });
}

export function useRoles(): UseQueryResult<RoleList> {
  return useQuery({
    queryKey: ROLES_QUERY_KEY,
    queryFn: () => getOne(ROLES_URL, isRoleList, 'role list'),
    retry: false,
  });
}

export function usePermissionCatalogue(): UseQueryResult<PermissionCatalogue> {
  return useQuery({
    queryKey: PERMISSIONS_QUERY_KEY,
    queryFn: () => getOne(PERMISSIONS_URL, isPermissionCatalogue, 'permission catalogue'),
    /* The catalogue is derived from the deployment's own code and cannot change while the tab is
     * open. Cached for the session rather than refetched per mount. */
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
}

export interface RoleChange {
  readonly subject: string;
  readonly roleNames: readonly string[];
}

export function useSetRoles(): UseMutationResult<TenantUser, Error, RoleChange> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ subject, roleNames }: RoleChange) =>
      put(
        `${USERS_URL}/${encodeURIComponent(subject)}/roles`,
        isTenantUser,
        'tenant user',
        { role_names: roleNames },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: USERS_QUERY_KEY });
      /* The roles table shows a holder count per role, so it moves when an assignment does. */
      void client.invalidateQueries({ queryKey: ROLES_QUERY_KEY });
      /* And the caller may have just changed their *own* roles, which changes what the nav should
       * offer. Re-reading identity is what stops the sidebar disagreeing with the server. */
      void client.invalidateQueries({ queryKey: IDENTITY_QUERY_KEY });
    },
  });
}

/** Removes a person from the tenant. 204, and there is nothing to read back.
 *
 * **A soft delete at the server, which is what makes it offerable at all.** The credential is
 * retired and its email address is permanently reserved; the subject itself survives, because the
 * audit chain references it for seven years and a row an entry names must stay resolvable.
 *
 * `remove` carries no `Idempotency-Key`, by that helper's own rule: the subject is in the URL, so a
 * retry is the same request by construction and a repeat of a completed delete is a 404 — the honest
 * answer rather than a replayed success.
 *
 * The roles table is invalidated alongside the user list because it carries a holder count per role,
 * and identity because an administrator can in principle delete the account they are signed in as —
 * the server refuses that with a 422, and re-reading is what keeps the nav from disagreeing with it
 * if that ever changes.
 */
export function useDeleteUser(): UseMutationResult<void, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (subject: string) =>
      remove(`${USERS_URL}/${encodeURIComponent(subject)}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: USERS_QUERY_KEY });
      void client.invalidateQueries({ queryKey: ROLES_QUERY_KEY });
      void client.invalidateQueries({ queryKey: IDENTITY_QUERY_KEY });
    },
  });
}

export interface StatusChange {
  readonly subject: string;
  readonly disabled: boolean;
}

export function useSetStatus(): UseMutationResult<TenantUser, Error, StatusChange> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ subject, disabled }: StatusChange) =>
      put(
        `${USERS_URL}/${encodeURIComponent(subject)}/status`,
        isTenantUser,
        'tenant user',
        { disabled },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: USERS_QUERY_KEY });
    },
  });
}
